/**
 * ModuleOffNotice — what a workspace admin sees instead of a silent redirect
 * when they open a route whose module is off.
 *
 * Why this exists (defect, 2026-09-02). Five preview modules ship
 * `defaultEnabled: false`, and every surface behind them failed CLOSED and
 * SILENT: /prioritization/decision-latency bounced to the dashboard, and a
 * commitment in the terms table rendered as plain text that swallowed the
 * click. Both are correct gate behaviour and both read, to the person in front
 * of the screen, as a broken product — the demo that surfaced this lost four
 * of its five stops to exactly this. A gate that is off should say so.
 *
 * It renders NO gated data — it names the module, says it is off, and links to
 * the toggle. Non-admins keep the redirect: someone who cannot turn a module on
 * gains nothing from being told it exists.
 */

import { Link } from 'react-router-dom';
import { ToggleLeft } from 'lucide-react';

export interface ModuleOffNoticeProps {
  /** Human name of the module, as it reads on the Modules settings page. */
  moduleName: string;
  /** One sentence: what the surface would show if it were on. */
  whatItDoes: string;
}

export function ModuleOffNotice({ moduleName, whatItDoes }: ModuleOffNoticeProps) {
  return (
    <div className="mx-auto max-w-xl px-6 py-20 text-center">
      <ToggleLeft className="mx-auto mb-4 h-8 w-8 text-[var(--text-ter)]" aria-hidden="true" />
      <h1 className="text-lg font-semibold text-[var(--text-pri)]">
        {moduleName} is off for this workspace
      </h1>
      <p className="mt-2 text-sm text-[var(--text-sec)]">{whatItDoes}</p>
      <p className="mt-1 text-sm text-[var(--text-ter)]">
        The page is built and ready — it needs the module turned on.
      </p>
      <Link
        to="/prioritization/settings/modules"
        className="mt-6 inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-pri)] transition-colors hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/50"
      >
        <ToggleLeft className="h-4 w-4" aria-hidden="true" />
        Open Settings › Modules
      </Link>
    </div>
  );
}

export default ModuleOffNotice;
